import React, { useState, useEffect } from 'react';
import { FaClock, FaTrash, FaEdit, FaPoll, FaImages, FaStream, FaFileAlt, FaCalendarAlt } from 'react-icons/fa';
import { scheduledAPI } from '../utils/api';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

const typeIcons = { post: FaFileAlt, poll: FaPoll, carousel: FaImages, thread: FaStream };
const typeLabels = { post: 'Point', poll: 'Poll', carousel: 'Carousel', thread: 'Talk' };

const formatScheduledDate = (isoStr) => {
  const d = new Date(isoStr);
  const now = new Date();
  const diffMs = d - now;
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffMins = Math.floor((diffMs % 3600000) / 60000);

  const dateStr = d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const timeStr = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  let relative = '';
  if (diffMs < 0) {
    relative = 'Publishing soon...';
  } else if (diffHrs < 1) {
    relative = `in ${diffMins}m`;
  } else if (diffHrs < 24) {
    relative = `in ${diffHrs}h ${diffMins % 60}m`;
  } else {
    relative = `in ${Math.floor(diffHrs / 24)}d`;
  }

  return { dateStr, timeStr, relative };
};

const Scheduled = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScheduled();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadScheduled = async () => {
    try {
      const res = await scheduledAPI.getAll();
      setPosts(res.data);
    } catch (e) {
      toast.error('Failed to load scheduled posts');
    } finally {
      setLoading(false);
    }
  };

  const cancelPost = async (postId) => {
    try {
      await scheduledAPI.cancel(postId);
      setPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Scheduled post cancelled');
    } catch (e) {
      toast.error('Failed to cancel');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <Sidebar />
        <main className="col-span-12 md:col-span-10" data-testid="scheduled-page">
          <div className="max-w-3xl mx-auto p-6">
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-10 h-10 bg-gradient-to-br from-[#4A90E2] to-[#A855F7] rounded-xl flex items-center justify-center">
                <FaClock className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-outfit font-bold text-gray-900">Scheduled Posts</h1>
                <p className="text-sm text-gray-500">Manage your upcoming content</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-16 text-gray-400">Loading...</div>
            ) : posts.length === 0 ? (
              <div className="text-center py-16" data-testid="no-scheduled-posts">
                <FaCalendarAlt className="text-5xl text-gray-200 mx-auto mb-4" />
                <p className="text-gray-500 font-medium text-lg">No scheduled posts</p>
                <p className="text-gray-400 text-sm mt-1">Schedule posts from the <span className="font-arthaus">Create Point</span> modal</p>
              </div>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => {
                  const Icon = typeIcons[post.post_type] || FaFileAlt;
                  const { dateStr, timeStr, relative } = formatScheduledDate(post.scheduled_at);
                  return (
                    <div
                      key={post.id}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:border-[#4A90E2]/30 transition-colors group"
                      data-testid={`scheduled-post-${post.id}`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-9 h-9 rounded-lg bg-[#4A90E2]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon className="text-[#4A90E2]" size={14} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`text-xs font-medium text-[#4A90E2] bg-[#4A90E2]/10 px-2 py-0.5 rounded-full ${(typeLabels[post.post_type] || 'Point') === 'Point' ? 'font-arthaus' : ''}`}>
                              {typeLabels[post.post_type] || 'Point'}
                            </span>
                            {post.platforms?.length > 0 && (
                              <span className="text-xs text-gray-400">
                                + {post.platforms.length} platform{post.platforms.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <p className="text-gray-800 text-sm line-clamp-2">{post.content}</p>
                          <div className="flex items-center space-x-2 mt-2">
                            <FaClock className="text-amber-500" size={11} />
                            <span className="text-xs text-gray-500">{dateStr} at {timeStr}</span>
                            <span className="text-xs font-medium text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">{relative}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => cancelPost(post.id)}
                          className="p-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all rounded-lg hover:bg-red-50"
                          data-testid={`cancel-scheduled-${post.id}`}
                        >
                          <FaTrash size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Scheduled;
